import { reloadProps } from "core/navigation"
import { Sentry } from "./sentry"

/** One reload per tab session when layout chrome hits stale/mismatched layoutConfig. */
export const LAYOUT_RELOAD_KEY = "inertia_layout_reload_attempted"

/**
 * Report the error to Sentry, then reload once to recover from stale /
 * incomplete layoutConfig. Uses a single sessionStorage flag so header and footer
 * share one attempt.
 *
 * Returns true if a reload was triggered, false if we already reloaded once
 * (caller should render fallback / null).
 */
export function reportAndReload(error, info, pageContext) {
  const { layoutConfig, url, component, version } = pageContext

  Sentry.withScope(scope => {
    scope.setTag("location", "InertiaLayout")
    scope.setContext("inertia_page", { url, component, version })
    scope.setContext("layout_config", layoutConfigForSentry(layoutConfig))
    scope.setContext("navigation", navigationContextForSentry())
    scope.setContext("react_error_info", {
      component_stack: info?.componentStack
    })
    scope.setContext("custom", { bypassFilters: true })
    Sentry.captureException(error)
  })

  if (sessionStorage.getItem(LAYOUT_RELOAD_KEY)) {
    return false
  }

  sessionStorage.setItem(LAYOUT_RELOAD_KEY, "1")
  // Re-fetch page + shared props (layoutConfig) without a full browser reload.
  reloadProps()
  return true
}

/** Clear after a successful layout render so a later deploy can recover again. */
export function clearLayoutReloadGuard() {
  sessionStorage.removeItem(LAYOUT_RELOAD_KEY)
}

/**
 * Navigation context for Sentry: answers "where did the user come from, and
 * was this the first page they opened in this tab?"
 */
export function navigationContextForSentry() {
  const navEntry = performance.getEntriesByType("navigation")[0]
  const navigationType = navEntry?.type ?? "unknown"
  const referrer = document.referrer

  return {
    navigation_type: navigationType,
    referrer: referrer || null,
    is_first_page_in_tab: navigationType === "navigate" && referrer === ""
  }
}

/**
 * Snapshot of Inertia `layoutConfig` for Sentry: round-trip through JSON so we
 * only send plain serializable data (matches what came over the wire).
 */
export function layoutConfigForSentry(layoutConfig) {
  if (layoutConfig == null) {
    return { present: false }
  }

  try {
    const json = JSON.stringify(layoutConfig)
    return {
      present: true,
      json,
      snapshot: JSON.parse(json)
    }
  } catch (e) {
    return {
      present: true,
      snapshot_unavailable: true,
      error: String(e?.message ?? e)
    }
  }
}
